import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../../AuthContext/AuthContext';
import MedicalCard from './MedicalCard';
import MedicalEditor from './MedicalEditor';
import { fetchMedicalData, saveMedicalData, deleteMedicalData } from './MedicalApiActions';
import { sanitizeForm } from '../../../Utilities/DataCleanUp';
import { REQUIRED_FIELDS } from '../../MainForm/config/formConfig';
import { useTranslation } from 'react-i18next';

const MedicalForm = ({ onSave, onValidationChange, data }) => {
    const { t } = useTranslation('medical');
    const { currentUser } = useAuth();
    const [medicalRecords, setMedicalRecords] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const fetchedRef = useRef(false);
    const lastValidatedDataRef = useRef(null);
    const successTimeoutRef = useRef(null);

    const validateRecords = useCallback((records) => {
        console.log(t('validation.passing_records_to_parent') + ":", records);
        const recordsString = JSON.stringify(records);
        if (recordsString === lastValidatedDataRef.current) {
            console.log(t('validation.records_unchanged'));
            return;
        }
        lastValidatedDataRef.current = recordsString;

        if (!Array.isArray(records) || records.length === 0) {
            console.log(t('validation.no_records_or_empty'));
            // For empty arrays, still pass the array to the parent
            if (Array.isArray(records)) {
                onValidationChange?.(records);
            } else {
                onValidationChange?.(false);
            }
            setFormErrors({});
            return false;
        }

        const requiredFields = REQUIRED_FIELDS.medical;
        const errors = {};
        let isValid = true;

        records.forEach((record, index) => {
            Object.entries(requiredFields).forEach(([field, weight]) => {
                const value = record[field];
                if (weight === 1.0 && (!value || value.toString().trim() === '')) {
                    errors[`${index}_${field}`] = t(`errors.${field}.required`);
                    isValid = false;
                }
            });
        });

        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) {
            setFormErrors(errors);
        }

        // IMPORTANT: Pass the array to parent, not just a boolean
        console.log(t('validation.passing_records_to_parent') + ":", records);
        onValidationChange?.(records);

        return isValid;
    }, [onValidationChange, formErrors, t]);

    useEffect(() => {
        if (!currentUser?.id || fetchedRef.current) return;

        let isMounted = true;
        const loadData = async () => {
            try {
                setLoading(true);
                const fetchedData = await fetchMedicalData(currentUser);
                if (isMounted) {
                    const arrayData = Array.isArray(fetchedData) ? fetchedData : [];
                    setMedicalRecords(arrayData);
                    validateRecords(arrayData);
                    fetchedRef.current = true;
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error loading medical data:', error);
                    setError(t('error.loading'));
                    setMedicalRecords([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, [currentUser?.id, validateRecords, t]);

    useEffect(() => {
        if (data) {
            const arrayData = Array.isArray(data) ? data : [];
            setMedicalRecords(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(arrayData)) {
                    validateRecords(arrayData);
                    return arrayData;
                }
                return prev;
            });
        }
    }, [data, validateRecords]);

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }
        };
    }, []);

    const handleEdit = useCallback((record) => {
        setEditingRecord(record);
        setIsEditing(true);
        setFormErrors({});
        setError(null);
    }, []);

    const handleAdd = useCallback(() => {
        setEditingRecord(null);
        setIsEditing(true);
        setFormErrors({});
        setError(null);
    }, []);

    const handleDelete = useCallback(async (recordId) => {
        try {
            console.log(t('operations.starting_delete') + ":", recordId);
            setLoading(true);
            await deleteMedicalData(recordId, currentUser);
            console.log(t('operations.deletion_successful') + ":", recordId);

            // Update local state with filtered records
            const updatedRecords = medicalRecords.filter(record => record.id !== recordId);
            console.log(t('operations.updated_after_deletion') + ":", updatedRecords);
            setMedicalRecords(updatedRecords);

            // Pass the updated array to parent
            console.log(t('operations.passing_updated_after_delete') + ":", updatedRecords);
            onSave?.('medical', updatedRecords);

            // Also call onValidationChange with the updated array
            onValidationChange?.(updatedRecords);

            setSuccess(true);
            successTimeoutRef.current = setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Delete error:', error);
            setError(t('error.deletion'));
        } finally {
            setLoading(false);
        }
    }, [currentUser, medicalRecords, onSave, onValidationChange, t]);

    const handleSave = async (updatedData) => {
        try {
            console.log(t('operations.starting_save') + ":", updatedData);
            setLoading(true);
            setError(null);

            const recordToSave = {
                ...updatedData[0],
                user_id: currentUser.id,
                id: editingRecord?.id
            };
            console.log("Record to save:", recordToSave);

            const sanitizedData = sanitizeForm(recordToSave);
            console.log("Sanitized data for save:", sanitizedData);

            await saveMedicalData(
                sanitizedData,
                currentUser,
                (section, savedRecords) => {
                    console.log(`Save callback received ${section} data:`, savedRecords);

                    // IMPORTANT: Check that we received an array
                    if (Array.isArray(savedRecords)) {
                        console.log("Updating medical records with array:", savedRecords);
                        // Update the local state with the saved records
                        setMedicalRecords(savedRecords);
                        // Pass the array to parent component
                        onSave(section, savedRecords);
                        // Call validation with the array
                        onValidationChange(savedRecords);
                    } else {
                        console.error("Did not receive array from saveMedicalData:", savedRecords);
                    }
                },
                () => {
                    setSuccess(true);
                    successTimeoutRef.current = setTimeout(() => setSuccess(false), 3000);
                },
                (errors) => setError(errors[0] || t('error.save'))
            );

            setIsEditing(false);
            setEditingRecord(null);
        } catch (error) {
            console.error('Save error:', error);
            setError(t('error.save'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="mt-4 mb-5">
            <h2 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#4682B4' }}>
                {t('title')}
            </h2>

            {loading && <Alert variant="info">{t('loading')}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
                <Alert variant="success">
                    {t(editingRecord ? 'success.update' : 'success.save')}
                </Alert>
            )}

            {!loading && (!Array.isArray(medicalRecords) || medicalRecords.length === 0) && !isEditing && (
                <>
                    <Alert variant="warning" className="text-center">
                        {t('no_records')}
                    </Alert>
                    <div className="d-flex justify-content-center">
                        <Button variant="success" onClick={handleAdd}>
                            <FaPlus className="me-2" /> {t('buttons.add_record')}
                        </Button>
                    </div>
                </>
            )}

            {!loading && Array.isArray(medicalRecords) && medicalRecords.length > 0 && !isEditing && (
                <>
                    <div className="d-flex justify-content-end mb-3">
                        <Button variant="success" onClick={handleAdd}>
                            <FaPlus className="me-2" /> {t('buttons.add_record')}
                        </Button>
                    </div>
                    {medicalRecords.map((record, index) => (
                        <MedicalCard
                            key={record.id || index}
                            medicalData={record}
                            currentUser={currentUser}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                        />
                    ))}
                </>
            )}

            {isEditing && (
                <MedicalEditor
                    currentUser={currentUser}
                    initialData={editingRecord}
                    onSave={handleSave}
                    onCancel={() => {
                        setIsEditing(false);
                        setEditingRecord(null);
                        setError(null);
                        setFormErrors({});
                    }}
                    formErrors={formErrors}
                />
            )}
        </Container>
    );
};

export default MedicalForm;